// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek <roman.morawek@embyt.com>

import * as React from "react";
import { connect } from "react-redux";
import { WrappedComponentProps, injectIntl, FormattedMessage, defineMessages } from "react-intl";
import { Modal, Button } from "react-bootstrap";

import { ChargerCommand } from "graphql/api";
import { isSet } from "utils";
import { ActionType, IAction, IActionChargerCommand, raiseAction } from "actions";
import { IState } from "states/states";
import { IRfid } from "states/ocppstates";
import { ModalPanel } from "states/guistates";
import { toDropdownItems } from "guiutils";

import Dropdown from "view/comp/Dropdown";

interface IModalStartTransactionExtProps {
  chargerId: number;
}

interface IModalStartTransactionProps {
  isOpen: boolean;
  connector: number | undefined;
  rfids: IRfid[];
}

const mapStateToProps = (state: IState) => {
  return {
    isOpen: state.gui.modal.panel === ModalPanel.START_TRANSACTION,
    connector: state.router.connector,
    rfids: state.ocpp.rfid,
  };
};

interface IModalStartTransactionState {
  selectedRfid: number | undefined;
}

interface IModalStartTransactionDispatch {
  onOkClick: (rfid: string, connector: number | undefined) => void;
  onCancelClick: () => void;
}

const mapDispatchToProps = (
  dispatch: (action: IAction<any>) => void,
  ownProps: IModalStartTransactionExtProps,
) => {
  const dispatchProps: IModalStartTransactionDispatch = {
    onOkClick: (rfid: string, connector: number | undefined) => {
      const payload: IActionChargerCommand = {
        chargerId: ownProps.chargerId,
        cmd: ChargerCommand.StartTransaction,
        cmdArgs: {
          connectorId: connector,
          idTag: rfid,
        },
        timestampMs: Date.now(),
      };
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
      dispatch(raiseAction(ActionType.ACT_TRIGGER_CHARGER_CMD_REQ, payload));
    },
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  rfidSelectionCaption: {
    id: "Select user",
  },
});

class ModalStartTransactionComponent extends React.Component<
  IModalStartTransactionProps & IModalStartTransactionDispatch & WrappedComponentProps,
  IModalStartTransactionState
> {
  public state: IModalStartTransactionState = {
    selectedRfid: undefined,
  };

  public render() {
    const rfid = isSet(this.state.selectedRfid)
      ? this.props.rfids[this.state.selectedRfid]
      : undefined;

    return (
      <Modal
        show={this.props.isOpen}
        onHide={this.props.onCancelClick}
        id="modal-start-transaction"
      >
        <Modal.Header closeButton>
          <FormattedMessage id="Starting charging session" />
        </Modal.Header>
        <Modal.Body>
          <p>
            <FormattedMessage id="Which user shall be associated to this charging session?" />
          </p>
          {this.renderRfidSelection()}
          <p className="mt-4">
            <FormattedMessage id="This will start a new charging session and authorize the selected user." />
          </p>
          <p className="mb-4">
            <FormattedMessage id="Please make sure that the vehicle is ready and already plugged to the charger." />
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            id="confirmCancel"
            variant="secondary"
            className="me-1"
            onClick={this.props.onCancelClick}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button
            id="confirmOk"
            variant="primary"
            disabled={!rfid}
            onClick={(ev) => this.props.onOkClick(rfid!.rfid, this.props.connector)}
          >
            <FormattedMessage id="Authorize Charging" />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  private renderRfidSelection() {
    const idToLabel = (index: number) =>
      this.props.rfids[index]?.name
        ? this.props.rfids[index].name!
        : this.props.rfids[index]?.rfid ?? "";
    const indexList = Array.from(Array(this.props.rfids.length).keys())
      .filter((curIndex) => this.props.rfids[curIndex].isActive)
      .sort((a, b) => idToLabel(a).localeCompare(idToLabel(b)));
    const rfidItems = toDropdownItems(
      indexList,
      "rfid",
      (_name, index) => this.setState({ selectedRfid: index }),
      idToLabel,
    );
    return (
      <Dropdown
        items={rfidItems}
        style={{
          maxHeight: "150px !important",
        }}
        curItem={
          isSet(this.state.selectedRfid)
            ? idToLabel(this.state.selectedRfid)
            : `<${this.props.intl.formatMessage(messages.rfidSelectionCaption)}>`
        }
      />
    );
  }
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(ModalStartTransactionComponent),
);
